/**
 * Lógica de tickets del POS legacy portada al frontend (misma que
 * pos-quasar/src/shared/taxesLogic.ts y ticketDiscount.ts).
 *
 * - calculateTicketTaxLines: árbol de impuestos Openbravo (cascada) para
 *   calcular los impuestos del ticket en pantalla.
 * - buildPercentDiscountLines: réplica de Button.Discount2 (una línea de
 *   descuento negativa por cada base imponible).
 */

/** Producto especial de descuento del tenant (Button.Discount2 / Openbravo). */
export const DISCOUNT_PRODUCT_ID = '11600';
export const DISCOUNT2_DEFAULT_RATE = 0.15;

const buildTaxTree = (taxes) => {
  const ordered = [...taxes].sort((a, b) => a.rateOrder - b.rateOrder);
  const trees = new Map();
  const orphans = new Map();

  for (const tax of ordered) {
    const node = { tax, sons: [] };
    let parent = tax.parentId ? trees.get(tax.parentId) : null;

    if (!parent && tax.parentId) {
      parent = orphans.get(tax.parentId) || null;
      if (!parent) {
        parent = { tax: null, sons: [] };
        orphans.set(tax.parentId, parent);
      }
    }

    if (parent) {
      parent.sons.push(node);
    }

    const orphanChildren = orphans.get(tax.id);
    if (orphanChildren) {
      node.sons.push(...orphanChildren.sons);
      orphans.delete(tax.id);
    }

    trees.set(tax.id, node);
  }

  return trees;
};

const sumTaxes = (lines) => lines.reduce((sum, line) => sum + line.amount, 0);

const calculateLineTaxes = (base, node) => {
  if (!node.tax) {
    return [];
  }

  if (node.sons.length === 0) {
    return [{ taxId: node.tax.id, base, amount: base * node.tax.rate }];
  }

  let accumulated = base;
  const result = [];

  for (const son of node.sons) {
    const subLines = calculateLineTaxes(node.tax.rateCascade ? accumulated : base, son);
    result.push(...subLines);
    accumulated += sumTaxes(subLines);
  }

  return result;
};

const mergeTaxLines = (target, incoming) => {
  for (const line of incoming) {
    const existing = target.find((item) => item.taxId === line.taxId);
    if (existing) {
      existing.base += line.base;
      existing.amount += line.amount;
    } else {
      target.push({ ...line });
    }
  }
  return target;
};

export const calculateTicketTaxLines = (lines, taxes) => {
  const trees = buildTaxTree(taxes);
  let aggregated = [];

  for (const line of lines) {
    const node = trees.get(line.taxId);
    if (!node) continue;
    aggregated = mergeTaxLines(aggregated, calculateLineTaxes(line.units * line.price, node));
  }

  return aggregated;
};

const percentLabel = (rate) => `${Math.round(rate * 10000) / 100}%`;

/**
 * Réplica de Button.Discount2: líneas de descuento negativas por base imponible.
 * @param {Array<{productId, units, price, taxId, isDiscountLine?}>} lines
 * @param {Array} taxes registros de TAXES
 * @param {{rate?: number}} options
 */
export const buildPercentDiscountLines = (lines, taxes, options = {}) => {
  const rate = options.rate != null ? options.rate : DISCOUNT2_DEFAULT_RATE;
  const productId = DISCOUNT_PRODUCT_ID;

  const saleLines = lines
    .filter((line) => !line.isDiscountLine && line.productId)
    .map((line) => ({ units: line.units, price: line.price, taxId: line.taxId }));

  const subtotal = saleLines.reduce((sum, line) => sum + line.units * line.price, 0);
  if (subtotal <= 0) {
    return [];
  }

  const taxLines = calculateTicketTaxLines(saleLines, taxes);
  if (!taxLines.length) {
    const amount = subtotal * rate;
    if (amount <= 0) {
      return [];
    }

    return [{
      productId,
      code: productId,
      name: `Descuento ${percentLabel(rate)} de ${subtotal.toFixed(2)}`,
      quantity: 1,
      unitPrice: -amount,
      taxId: (saleLines[0] && saleLines[0].taxId) || '000',
      taxCat: '000',
      taxRate: 0,
      isDiscountLine: true,
    }];
  }

  return taxLines
    .filter((taxLine) => taxLine.base > 0)
    .map((taxLine) => {
      const amount = taxLine.base * rate;
      const taxRecord = taxes.find((tax) => tax.id === taxLine.taxId);

      return {
        productId,
        code: productId,
        name: `Descuento ${percentLabel(rate)} de ${taxLine.base.toFixed(2)}`,
        quantity: 1,
        unitPrice: -amount,
        taxId: taxLine.taxId,
        taxCat: (taxRecord && taxRecord.category) || '000',
        taxRate: (taxRecord && taxRecord.rate) || 0,
        isDiscountLine: true,
      };
    });
};

/** Nombre del producto de descuento usado al persistir (ATTRIBUTES). */
export const DISCOUNT_PRODUCT_NAME = 'DESCUENTO NO EDITAR';
